import { getRequest, postRequest } from '../../../core/utils/api';
import { type KeycloakContext } from '../../authentication/keycloak/KeycloakProvider';
import type AgendaEntry from '../models/agenda-entry';
import type AgendaEntryUpsert from '../models/agenda-entry-upsert';

/** What the save did, or why it did not. */
export interface UpsertAgendaEntryOutcome {
  readonly saved: boolean;
  /**
   * The server's explanation when it refused. Shown to the administrator as-is: the API answers
   * with a sentence naming the actual cause — which colleague has no access to this office, which
   * room is full — and replacing it with a generic message here would throw away the only thing
   * that tells them what to do next.
   */
  readonly error?: string;
}

export const upsertAgendaEntry = async (
  agendaEntry: AgendaEntryUpsert,
  keycloak: KeycloakContext,
): Promise<UpsertAgendaEntryOutcome> => {
  const apiResponse = await postRequest<AgendaEntryUpsert>('agenda-entry', keycloak, agendaEntry);

  if (apiResponse.error) {
    return { saved: false, error: apiResponse.error };
  }

  return { saved: true };
};

export const getAgendaEntryForUser = async (
  officeId: string,
  email: string,
  date: string,
  keycloak: KeycloakContext,
): Promise<AgendaEntry | undefined> => {
  const apiResponse = await getRequest<AgendaEntry>(`agenda-entry/for-user?officeId=${officeId}&email=${email}&date=${date}`, keycloak);

  if (apiResponse.error) {
    return undefined;
  }
  return apiResponse.json;
};

export const getAgendaEntriesByMonthForUser = async (
  officeId: string,
  email: string,
  date: string,
  keycloak: KeycloakContext,
): Promise<AgendaEntry[] | undefined> => {
  const apiResponse = await getRequest<AgendaEntry[]>(
    `agenda-entry/get-agenda-entries-for-month?officeId=${officeId}&email=${email}&date=${date}`,
    keycloak,
  );
  if (apiResponse.error) {
    return undefined;
  }
  return apiResponse.json;
};
